import React from "react";
import {
  AbsoluteFill,
  Img,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { LOOK, SAFE, TYPE } from "../look";
import { Struck } from "../ui/bits";
import { Words } from "../ui/Words";
import { Sfx, Ticks } from "../../b2b/ui/Sfx";

/**
 * Scene 4 — no gatekeeping. The /vs LTK page's core line ("every customer
 * can earn, not just the curated few") said the consumer way: cross out
 * the follower count, show the people who actually listen to you.
 */

type Friend = { src?: string; initial?: string; tint?: string };

const FRIENDS: Friend[] = [
  { src: "images/scene1/2.jpg" },
  { initial: "M", tint: LOOK.orange },
  { src: "images/scene1/4.jpg" },
  { initial: "J", tint: LOOK.green },
  { src: "images/scene1/6.jpg" },
];

const AVATAR = 128;

export const Followers: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();

  const STRUCK_IN = 5;
  const STRIKE_AT = 20;
  const ANSWER_AT = 30;

  const struckIn = spring({
    frame: frame - STRUCK_IN,
    fps,
    config: { damping: 18, stiffness: 200, mass: 0.7 },
  });

  return (
    <AbsoluteFill
      style={{
        background: LOOK.white,
        alignItems: "center",
        justifyContent: "center",
        padding: SAFE,
        gap: 8,
      }}
    >
      <Words text={copy.followers.lead} fontSize={TYPE.headline} />
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: TYPE.headline,
          letterSpacing: -3.5,
          lineHeight: 1.1,
          color: LOOK.ink,
          padding: "18px 20px 0",
        }}
      >
        <div
          style={{
            translate: `0 ${(1 - struckIn) * 60}px`,
            opacity: struckIn,
          }}
        >
          <Struck at={STRIKE_AT} thickness={14}>
            {copy.followers.struck}
          </Struck>
        </div>
      </div>

      <div style={{ height: 70 }} />

      <Words
        text={copy.followers.answer}
        start={ANSWER_AT}
        fontSize={TYPE.title + 4}
        color={LOOK.blue}
      />

      {/* The people who actually listen to you */}
      <div style={{ display: "flex", marginTop: 44 }}>
        {FRIENDS.map((f, i) => {
          const p = spring({
            frame: frame - ANSWER_AT - 6 - i * 3,
            fps,
            config: { damping: 11, stiffness: 240, mass: 0.6 },
          });
          return (
            <div
              key={i}
              style={{
                width: AVATAR,
                height: AVATAR,
                borderRadius: AVATAR,
                marginLeft: i === 0 ? 0 : -28,
                border: `6px solid ${LOOK.white}`,
                overflow: "hidden",
                background: f.tint,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: LOOK.white,
                fontFamily: LOOK.display,
                fontWeight: LOOK.bold,
                fontSize: 56,
                scale: p,
                boxShadow: "0 10px 24px -8px rgba(0,0,0,0.25)",
                zIndex: FRIENDS.length - i,
              }}
            >
              {f.src ? (
                <Img
                  src={staticFile(f.src)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                f.initial
              )}
            </div>
          );
        })}
      </div>

      <Sfx sound="strike" at={STRIKE_AT + 2} />
      <Ticks from={ANSWER_AT + 6} to={ANSWER_AT + 6 + FRIENDS.length * 3} every={3} volume={1.2} />
    </AbsoluteFill>
  );
};
